import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, CheckSquare, Calendar, Bell, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const TasksCalendar = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/documentation')} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Docs
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <CheckSquare className="w-8 h-8 text-indigo-600" />
            <h1 className="text-4xl font-bold text-slate-900">Tasks and Calendar</h1>
          </div>
          <p className="text-lg text-slate-600">Stay organized and never miss a follow-up with integrated task management and calendaring.</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-indigo-600" />
                Task Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                Tasks help you track follow-ups, reminders, and action items related to your leads and deals. Each task includes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Title and detailed description</li>
                <li>Due date and time</li>
                <li>Priority level (Low, Medium, High)</li>
                <li>Assignment to team members</li>
                <li>Association with leads or companies</li>
                <li>Status tracking (To Do, In Progress, Done)</li>
              </ul>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <p className="text-sm text-slate-700">
                  <strong>Pro Tip:</strong> Create tasks directly from lead detail pages for quick follow-up scheduling.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Calendar View
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                The Calendar provides a visual overview of all your scheduled tasks and activities:
              </p>
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Day View</h3>
                  <p className="text-sm text-slate-600">See hour-by-hour breakdown of your daily schedule</p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Week View</h3>
                  <p className="text-sm text-slate-600">Plan your entire week at a glance</p>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Month View</h3>
                  <p className="text-sm text-slate-600">See all upcoming tasks and deadlines</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-orange-600" />
                Reminders & Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                Never miss an important task with automatic reminders:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li>Email notifications for upcoming due dates</li>
                <li>In-app notification bell alerts</li>
                <li>Configurable reminder timing (15 min, 1 hour, 1 day before)</li>
                <li>Overdue task highlighting</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                Team Collaboration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Assign tasks to specific team members</li>
                <li>View team calendar to see everyone's workload</li>
                <li>Add comments and updates to tasks</li>
                <li>Track task completion rates across your team</li>
              </ul>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-slate-700">
                  <strong>Best Practice:</strong> Regular task reviews during team meetings help ensure nothing falls through the cracks.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3 pt-6">
            <Button onClick={() => navigate('/documentation')} className="bg-indigo-600 hover:bg-indigo-700">
              Browse All Documentation
            </Button>
            <Button variant="outline" onClick={() => navigate('/contact')}>
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasksCalendar;
